<template>
  <div class="gw-dash">
    <section class="gw-dash__hero">
      <div>
        <p class="gw-dash__eyebrow">WhatsApp Gateway</p>
        <h2 class="gw-dash__hello">Selamat datang, {{ user.name || "Operator" }}</h2>
        <p class="gw-dash__lead">
          Pantau sesi, kontak, dan antrian pengiriman dari satu konsol.
        </p>
      </div>
      <div class="gw-dash__hero-actions">
        <button class="gw-dash__btn gw-dash__btn--primary" @click="go('pesan-keluar')">
          Kirim pesan
        </button>
        <button
          v-if="isAdmin"
          class="gw-dash__btn"
          @click="go('utility-tiket')"
        >
          Open Ticket
        </button>
        <button class="gw-dash__btn" @click="go('master-pengaturan')">
          Kelola sesi
        </button>
      </div>
    </section>

    <section class="gw-dash__metrics" aria-label="Ringkasan gateway">
      <article
        v-for="card in metricCards"
        :key="card.key"
        class="gw-dash__metric"
      >
        <div class="gw-dash__metric-icon" :style="{ background: card.tone }">
          <v-icon small color="#0d4a36">{{ card.icon }}</v-icon>
        </div>
        <div>
          <div class="gw-dash__metric-label">{{ card.label }}</div>
          <div class="gw-dash__metric-value">{{ formatNumber(card.value) }}</div>
          <div class="gw-dash__metric-hint">{{ card.hint }}</div>
        </div>
      </article>
    </section>

    <section v-if="isAdmin" class="gw-dash__ticket-metrics" aria-label="Ringkasan tiket">
      <article
        v-for="card in ticketCards"
        :key="card.key"
        class="gw-dash__metric gw-dash__metric--click"
        @click="go('utility-tiket')"
      >
        <div class="gw-dash__metric-icon" :style="{ background: card.tone }">
          <v-icon small color="#0d4a36">{{ card.icon }}</v-icon>
        </div>
        <div>
          <div class="gw-dash__metric-label">{{ card.label }}</div>
          <div class="gw-dash__metric-value">{{ formatNumber(card.value) }}</div>
          <div class="gw-dash__metric-hint">{{ card.hint }}</div>
        </div>
      </article>
    </section>

    <section class="gw-dash__grid">
      <div class="gw-dash__main-col">
        <div class="gw-dash__panel">
          <header class="gw-dash__panel-head">
            <div>
              <h3 class="gw-dash__panel-title">Aktivitas pesan</h3>
              <p class="gw-dash__panel-desc">Pengiriman terbaru dari outbox</p>
            </div>
            <button class="gw-dash__link" @click="go('pesan-keluar')">Lihat semua</button>
          </header>

          <div v-if="loadingStats" class="gw-dash__empty">Memuat data…</div>
          <div v-else-if="!recentMessages.length" class="gw-dash__empty">
            Belum ada pesan keluar. Mulai kirim dari menu Pesan Keluar.
          </div>
          <ul v-else class="gw-dash__list">
            <li v-for="item in recentMessages" :key="item.id" class="gw-dash__list-item">
              <div class="gw-dash__list-main">
                <div class="gw-dash__list-title">{{ item.recipient || "-" }}</div>
                <div class="gw-dash__list-sub">{{ truncate(item.content, 72) }}</div>
              </div>
              <div class="gw-dash__list-meta">
                <span
                  class="gw-dash__badge"
                  :class="item.sended || item.status === 'sended' ? 'is-ok' : 'is-wait'"
                >
                  {{ item.sended || item.status === 'sended' ? "Terkirim" : "Antrian" }}
                </span>
                <span class="gw-dash__time">{{ item.created_at }}</span>
              </div>
            </li>
          </ul>
        </div>

        <div v-if="isAdmin" class="gw-dash__panel">
          <header class="gw-dash__panel-head">
            <div>
              <h3 class="gw-dash__panel-title">Open Ticket</h3>
              <p class="gw-dash__panel-desc">
                Konfirmasi pembayaran &amp; pengaduan terbaru
              </p>
            </div>
            <button class="gw-dash__link" @click="go('utility-tiket')">Kelola tiket</button>
          </header>

          <div v-if="loadingStats" class="gw-dash__empty">Memuat data…</div>
          <div v-else-if="!recentTickets.length" class="gw-dash__empty">
            Belum ada tiket masuk.
          </div>
          <ul v-else class="gw-dash__list">
            <li
              v-for="item in recentTickets"
              :key="item.id"
              class="gw-dash__list-item gw-dash__list-item--click"
              @click="go('utility-tiket')"
            >
              <div class="gw-dash__list-main">
                <div class="gw-dash__list-title">{{ item.subject || "-" }}</div>
                <div class="gw-dash__list-sub">
                  {{ item.category_label }}
                  <span v-if="item.user_name"> · {{ item.user_name }}</span>
                </div>
              </div>
              <div class="gw-dash__list-meta">
                <span
                  class="gw-dash__badge"
                  :class="ticketBadgeClass(item.status)"
                >
                  {{ item.status_label }}
                </span>
                <span class="gw-dash__time">{{ item.tanggal }}</span>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div class="gw-dash__side">
        <div class="gw-dash__panel">
          <header class="gw-dash__panel-head">
            <div>
              <h3 class="gw-dash__panel-title">Sesi perangkat</h3>
              <p class="gw-dash__panel-desc">Nomor gateway yang terdaftar</p>
            </div>
            <button class="gw-dash__link" @click="go('master-pengaturan')">Kelola</button>
          </header>

          <div v-if="!sessions.length" class="gw-dash__empty">
            Belum ada sesi. Daftarkan nomor di Pengaturan.
          </div>
          <ul v-else class="gw-dash__list">
            <li v-for="session in sessions" :key="session.id" class="gw-dash__list-item">
              <div class="gw-dash__list-main">
                <div class="gw-dash__list-title">{{ session.name }}</div>
                <div class="gw-dash__list-sub">{{ session.phone_number }}</div>
              </div>
              <span
                class="gw-dash__badge"
                :class="session.status === 2 ? 'is-ok' : session.status === 1 ? 'is-wait' : 'is-off'"
              >
                {{ sessionStatus(session.status) }}
              </span>
            </li>
          </ul>
        </div>

        <div class="gw-dash__panel gw-dash__shortcuts">
          <h3 class="gw-dash__panel-title">Aksi cepat</h3>
          <div class="gw-dash__shortcut-grid">
            <button
              v-for="action in shortcuts"
              :key="action.route"
              class="gw-dash__shortcut"
              @click="go(action.route)"
            >
              <v-icon small color="#1f7a57">{{ action.icon }}</v-icon>
              <span>{{ action.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: "dashboard",

  data: () => ({
    loadingStats: false,
    stats: {
      sessions: 0,
      sessions_active: 0,
      contacts: 0,
      messages: 0,
      messages_waiting: 0,
      messages_sent: 0,
      tickets: 0,
      tickets_open: 0,
      tickets_progress: 0,
      tickets_done: 0,
    },
    recentMessages: [],
    recentTickets: [],
    sessions: [],
  }),
  computed: {
    ...mapState(["theme", "http", "device", "loading", "user"]),
    isAdmin() {
      return this.user && this.user.authent === "superadmin";
    },
    shortcuts() {
      const items = [
        { label: "Pesan Keluar", route: "pesan-keluar", icon: "mdi-message-plus-outline" },
        { label: "Tester Group", route: "pesan-group-tester", icon: "mdi-account-group" },
        { label: "Kontak", route: "master-contact", icon: "mdi-contacts" },
        { label: "Pengaturan", route: "master-pengaturan", icon: "mdi-cog-outline" },
      ];
      if (this.isAdmin) {
        items.unshift({
          label: "Open Ticket",
          route: "utility-tiket",
          icon: "mdi-ticket-outline",
        });
      }
      return items;
    },
    metricCards() {
      return [
        {
          key: "sessions",
          label: "Sesi WA",
          value: this.stats.sessions,
          hint: `${this.stats.sessions_active} aktif`,
          icon: "mdi-cellphone-wireless",
          tone: "rgba(143, 214, 181, 0.35)",
        },
        {
          key: "contacts",
          label: "Kontak",
          value: this.stats.contacts,
          hint: "Buku alamat gateway",
          icon: "mdi-account-group-outline",
          tone: "rgba(143, 214, 181, 0.22)",
        },
        {
          key: "sent",
          label: "Terkirim",
          value: this.stats.messages_sent,
          hint: "Pesan sukses dikirim",
          icon: "mdi-check-decagram-outline",
          tone: "rgba(31, 122, 87, 0.12)",
        },
        {
          key: "queue",
          label: "Antrian",
          value: this.stats.messages_waiting,
          hint: `Total outbox ${this.formatNumber(this.stats.messages)}`,
          icon: "mdi-timer-sand",
          tone: "rgba(201, 221, 211, 0.65)",
        },
      ];
    },
    ticketCards() {
      return [
        {
          key: "tickets_open",
          label: "Menunggu",
          value: this.stats.tickets_open,
          hint: "Perlu ditindaklanjuti",
          icon: "mdi-clock-outline",
          tone: "rgba(255, 193, 7, 0.22)",
        },
        {
          key: "tickets_progress",
          label: "Diproses",
          value: this.stats.tickets_progress,
          hint: "Sedang ditangani",
          icon: "mdi-progress-clock",
          tone: "rgba(66, 133, 244, 0.18)",
        },
        {
          key: "tickets_done",
          label: "Selesai",
          value: this.stats.tickets_done,
          hint: "Sudah ditutup",
          icon: "mdi-check-circle-outline",
          tone: "rgba(31, 122, 87, 0.14)",
        },
        {
          key: "tickets",
          label: "Total Tiket",
          value: this.stats.tickets,
          hint: "Semua open ticket",
          icon: "mdi-ticket-outline",
          tone: "rgba(143, 214, 181, 0.28)",
        },
      ];
    },
  },
  created() {
    this.setPage({
      crud: true,
      dataUrl: "api/dashboard",
      pagination: false,
      title: "Dashboard",
      subtitle: "Ringkasan operasional WhatsApp gateway Anda",
      showtable: false,
      breadcrumbs: [
        { text: "Console", disabled: false, href: "" },
        { text: "Dashboard", disabled: true, href: "" },
      ],
    });

    this.fetchDashboard();
  },
  methods: {
    ...mapActions(["setPage"]),

    go(name) {
      this.$router.push({ name });
    },

    formatNumber(value) {
      const num = Number(value || 0);
      return new Intl.NumberFormat("id-ID").format(num);
    },

    truncate(text, max) {
      const value = String(text || "");
      if (value.length <= max) return value;
      return value.slice(0, max - 1) + "…";
    },

    sessionStatus(status) {
      if (status === 2) return "Aktif";
      if (status === 1) return "Pending";
      return "Nonaktif";
    },

    ticketBadgeClass(status) {
      if (status === "done") return "is-ok";
      if (status === "progress") return "is-wait";
      return "is-off";
    },

    fetchDashboard: async function () {
      this.loadingStats = true;
      try {
        const { data } = await this.http.get("api/dashboard");
        this.stats = {
          sessions: 0,
          sessions_active: 0,
          contacts: 0,
          messages: 0,
          messages_waiting: 0,
          messages_sent: 0,
          tickets: 0,
          tickets_open: 0,
          tickets_progress: 0,
          tickets_done: 0,
          ...(data.data || {}),
        };
        this.recentMessages = data.recent_messages || [];
        this.recentTickets = data.recent_tickets || [];
        this.sessions = data.sessions || [];
      } catch (error) {
        this.stats = {
          sessions: 0,
          sessions_active: 0,
          contacts: 0,
          messages: 0,
          messages_waiting: 0,
          messages_sent: 0,
          tickets: 0,
          tickets_open: 0,
          tickets_progress: 0,
          tickets_done: 0,
        };
        this.recentMessages = [];
        this.recentTickets = [];
        this.sessions = [];
      } finally {
        this.loadingStats = false;
      }
    },
  },
};
</script>

<style>
.gw-dash {
  --gw-ink: #10241c;
  --gw-forest: #0d4a36;
  --gw-leaf: #1f7a57;
  --gw-mint: #8fd6b5;
  --gw-fog: #eef6f1;
  --gw-paper: #f8fbf9;
  --gw-line: #c9ddd3;
  --gw-muted: #5d7268;
  font-family: "Source Sans 3", "Montserrat", sans-serif;
  color: var(--gw-ink);
  display: grid;
  gap: 1.1rem;
}

.gw-dash__hero {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-end;
  padding: 1.35rem 1.4rem;
  border-radius: 16px;
  color: #f4fbf7;
  background:
    radial-gradient(90% 120% at 100% 0%, rgba(143, 214, 181, 0.35), transparent 50%),
    linear-gradient(135deg, #0b3d2e 0%, #0f5c45 55%, #0a3327 100%);
  animation: gw-dash-fade 0.45s ease-out;
}

.gw-dash__eyebrow {
  margin: 0 0 0.4rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--gw-mint);
}

.gw-dash__hello {
  margin: 0;
  font-family: "Outfit", "Montserrat", sans-serif;
  font-size: clamp(1.35rem, 2.4vw, 1.85rem);
  font-weight: 700;
  letter-spacing: -0.02em;
}

.gw-dash__lead {
  margin: 0.45rem 0 0;
  max-width: 34rem;
  font-size: 0.95rem;
  color: rgba(244, 251, 247, 0.78);
}

.gw-dash__hero-actions {
  display: flex;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.gw-dash__btn {
  border: 1px solid rgba(255, 255, 255, 0.22);
  background: rgba(255, 255, 255, 0.08);
  color: #f4fbf7;
  border-radius: 10px;
  padding: 0.65rem 1rem;
  font: inherit;
  font-weight: 600;
  font-size: 0.88rem;
  cursor: pointer;
}

.gw-dash__btn--primary {
  background: #f4fbf7;
  color: var(--gw-forest);
  border-color: #f4fbf7;
}

.gw-dash__btn:hover {
  filter: brightness(1.05);
}

.gw-dash__metrics,
.gw-dash__ticket-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.85rem;
}

.gw-dash__main-col {
  display: grid;
  gap: 0.85rem;
  align-content: start;
}

.gw-dash__metric {
  display: flex;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 1rem 1.05rem;
  border-radius: 14px;
  background: #fff;
  border: 1px solid var(--gw-line);
  animation: gw-dash-fade 0.5s ease-out both;
}

.gw-dash__metric--click {
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.gw-dash__metric--click:hover {
  border-color: var(--gw-mint);
  box-shadow: 0 4px 14px rgba(13, 74, 54, 0.06);
}

.gw-dash__list-item--click {
  cursor: pointer;
}

.gw-dash__list-item--click:hover .gw-dash__list-title {
  color: var(--gw-leaf);
}

.gw-dash__metric:nth-child(2) { animation-delay: 0.05s; }
.gw-dash__metric:nth-child(3) { animation-delay: 0.1s; }
.gw-dash__metric:nth-child(4) { animation-delay: 0.15s; }

.gw-dash__metric-icon {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
}

.gw-dash__metric-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--gw-muted);
}

.gw-dash__metric-value {
  margin-top: 0.15rem;
  font-family: "Outfit", "Montserrat", sans-serif;
  font-size: 1.55rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  line-height: 1.1;
}

.gw-dash__metric-hint {
  margin-top: 0.2rem;
  font-size: 0.75rem;
  color: var(--gw-muted);
}

.gw-dash__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 0.9fr);
  gap: 0.85rem;
}

.gw-dash__side {
  display: grid;
  gap: 0.85rem;
  align-content: start;
}

.gw-dash__panel {
  background: #fff;
  border: 1px solid var(--gw-line);
  border-radius: 14px;
  padding: 1rem 1.1rem 0.85rem;
}

.gw-dash__panel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.gw-dash__panel-title {
  margin: 0;
  font-family: "Outfit", "Montserrat", sans-serif;
  font-size: 1.05rem;
  font-weight: 600;
}

.gw-dash__panel-desc {
  margin: 0.2rem 0 0;
  font-size: 0.82rem;
  color: var(--gw-muted);
}

.gw-dash__link {
  border: 0;
  background: transparent;
  color: var(--gw-leaf);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.gw-dash__empty {
  padding: 1.4rem 0.4rem 1.2rem;
  text-align: center;
  color: var(--gw-muted);
  font-size: 0.9rem;
}

.gw-dash__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.gw-dash__list-item {
  display: flex;
  justify-content: space-between;
  gap: 0.85rem;
  align-items: flex-start;
  padding: 0.75rem 0;
  border-top: 1px solid var(--gw-fog);
}

.gw-dash__list-item:first-child {
  border-top: 0;
}

.gw-dash__list-title {
  font-weight: 600;
  font-size: 0.92rem;
}

.gw-dash__list-sub {
  margin-top: 0.2rem;
  font-size: 0.82rem;
  color: var(--gw-muted);
  line-height: 1.4;
}

.gw-dash__list-meta {
  display: grid;
  justify-items: end;
  gap: 0.35rem;
  flex-shrink: 0;
}

.gw-dash__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: var(--gw-fog);
  color: var(--gw-muted);
}

.gw-dash__badge.is-ok {
  background: rgba(143, 214, 181, 0.35);
  color: var(--gw-forest);
}

.gw-dash__badge.is-wait {
  background: rgba(201, 221, 211, 0.8);
  color: #4d6358;
}

.gw-dash__badge.is-off {
  background: #f4e8e6;
  color: #8a3a32;
}

.gw-dash__time {
  font-size: 0.72rem;
  color: var(--gw-muted);
}

.gw-dash__shortcut-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  margin-top: 0.85rem;
}

.gw-dash__shortcut {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.8rem;
  border-radius: 12px;
  border: 1px solid var(--gw-line);
  background: var(--gw-paper);
  color: var(--gw-ink);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.gw-dash__shortcut:hover {
  border-color: var(--gw-mint);
  background: #fff;
}

@keyframes gw-dash-fade {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 1100px) {
  .gw-dash__metrics,
  .gw-dash__ticket-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .gw-dash__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .gw-dash__hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .gw-dash__metrics,
  .gw-dash__ticket-metrics {
    grid-template-columns: 1fr;
  }
}
</style>
